'use client';

import { useWalletHasBadge } from 'src/hooks/useTitanBadge';
import Tag from '../primitives/Tag/Tag';
import { cj } from 'src/utils/classNames';

interface TitanBadgeProps {
	/**
	 * Custom className for the badge container
	 */
	className?: string;
	/**
	 * Custom className for the badge text
	 */
	textClassName?: string;
	/**
	 * Show loading state instead of badge when data is being fetched
	 */
	showLoadingState?: boolean;
}

const TitanBadge: React.FC<TitanBadgeProps> = ({
	className,
	textClassName,
	showLoadingState = false,
}) => {
	const { data: hasBadgeData, isLoading: isCheckingBadge } =
		useWalletHasBadge();

	// Determine if wallet has earned the badge
	const hasEarnedBadge = hasBadgeData?.has_badge === true;
	const badgeQuerySucceeded = hasBadgeData?.success === true;

	// Don't render anything while loading unless showLoadingState is true
	if (isCheckingBadge && !showLoadingState) {
		return null;
	}

	// Don't render if query failed
	if (!badgeQuerySucceeded && !isCheckingBadge) {
		return null;
	}

	// Fixed compact variant styles since only compact is used
	const styles = {
		container: 'py-1 px-2 gap-xs',
		imageSize: 'h-3',
	};

	if (isCheckingBadge) {
		return (
			<Tag
				className={cj(
					'bg-button-secondary-bg text-text-secondary',
					styles.container,
					className
				)}
			>
				<div
					className={cj(
						'animate-pulse rounded bg-gray-400',
						styles.imageSize,
						'w-4'
					)}
				/>
				<span
					className={cj('whitespace-nowrap text-text-default', textClassName)}
				>
					Loading...
				</span>
			</Tag>
		);
	}

	if (hasEarnedBadge) {
		return (
			<Tag
				className={cj(
					'bg-button-selector text-text-default',
					styles.container,
					className
				)}
			>
				<img
					src="/assets/images/swap/titan-badge.webp"
					className={cj('inline', styles.imageSize)}
					alt="Titan Badge"
				/>
				<span className={cj('whitespace-nowrap', textClassName)}>
					Titan Badge Earned
				</span>
			</Tag>
		);
	}

	return (
		<Tag
			className={cj(
				'bg-tooltip-bg text-text-default',
				styles.container,
				className
			)}
		>
			<img
				src="/assets/images/swap/titan-badge-grey.webp"
				className={cj('inline mix-blend-luminosity', styles.imageSize)}
				alt="Titan Badge (Not Earned)"
			/>
			<span className={cj('whitespace-nowrap', textClassName)}>
				Badge Not Earned
			</span>
		</Tag>
	);
};

export default TitanBadge;
